import React from "react";
import { t } from "i18next";

import { AppColumn, AppRow } from "../../../UI/Layouts";
import { SizedBox } from "../../../UI/Sizes";
import useTheme from "../../../../hooks/useTheme";
import AppText from "../../../UI/Texts/AppTexts";
import { withOpacity, getTimeStamp, getUserName } from "../../../../utils/helperFunctions";
import { Icon } from "semantic-ui-react";
import { useResponsive } from "../../../UI/ResponsiveContext";
import { UserAvatar } from "../../../UI/DisplayImage";

export const MessageBubbleCard = ({
  key,
  messageData,
  currentUserId,
  profilesData,
  onReplyInThread,
  showReplyInThread = true,
}) => {
  const { isMobile } = useResponsive();
  const theme = useTheme();
  const senderId = messageData?.userId || messageData?.senderId || messageData?.uid;
  const fileData = messageData?.contentData?.metaData?.fileData || messageData?.contentPreview?.fileData;
  const messageText = messageData?.contentData?.text || messageData?.content;
  const hasText = messageText && messageText.length > 0;

  const senderProfile = profilesData?.[senderId];
  const isCurrentuser = senderId === currentUserId ||
    senderProfile?.userId === currentUserId;
  const senderName = senderProfile?.name || getUserName(senderProfile) || "";
  const senderProfileUrl = senderProfile?.profileImageUrl;

  const messagesCount = messageData?.messagesCount || 0;
  const hasReplies = messagesCount > 0;

  const handleReplyClick = () => onReplyInThread?.(messageData);

  return (
    <AppRow
      key={key}
      mainAxisAlignment={isCurrentuser ? "end" : "start"}
      crossAxisAlignment={"end"}
      style={{ marginBottom: theme.spacing.elementPadding, alignItems: "flex-start" }}
    >
      {!isCurrentuser && (
        <div style={{ marginRight: "0.75rem", marginTop: "0.8rem", marginLeft: "0.25rem" }}>
          <UserAvatar
            name={senderName}
            url={senderProfileUrl}
            style={{ height: "36px", width: "36px", flexShrink: 0 }}
          />
        </div>
      )}
      <div>
        {fileData && (
          <AppRow mainAxisAlignment={isCurrentuser ? "end" : "start"}>
            <AppRow style={{ backgroundColor: theme.colors.lightGrey, padding: theme.spacing.elementPadding, margin: isMobile && theme.spacing.elementPaddingSmall, borderRadius: "10px" }}>
              {(() => {
                const fileType = fileData?.mimeType || fileData?.contentType;
                return fileType?.includes("image")
                  ? <Icon name="file image outline" />
                  : <Icon name="file alternate outline" />;
              })()}
              <SizedBox width={theme.spacing.elementPaddingSmall} />
              <AppText.Footnote fontWeight={600}>{fileData.fileName}</AppText.Footnote>
            </AppRow>
          </AppRow>
        )}
        {hasText && (
          <AppColumn
            style={{
              padding: theme.spacing.elementPadding,
              borderRadius: theme.radius.default,
              minWidth: "40px",
              maxWidth: isMobile ? "300px" : "500px",
              minHeight: "40px",
              border: "none",
              backgroundColor: isCurrentuser ? theme.primaryColor : withOpacity(theme.colors.lightGrey, "0.6"),
            }}
          >
            <AppText.Caption color={isCurrentuser && theme.colors.white}>{messageText}</AppText.Caption>
          </AppColumn>
        )}
        <SizedBox height={theme.spacing.elementPaddingSmall} />
        <AppRow
          mainAxisAlignment={isCurrentuser ? "end" : "start"}
          style={{ paddingLeft: theme.spacing.elementPadding, paddingRight: theme.spacing.elementPadding }}
        >
          <AppText.FootnoteSmall color={theme.colors.grey}>
            {getTimeStamp(messageData.createdAt)}
          </AppText.FootnoteSmall>
        </AppRow>

        {/* Reply in thread link for ungrouped messages */}
        {showReplyInThread && hasReplies && onReplyInThread && (
          <>
            <SizedBox height={theme.spacing.elementPaddingSmall} />
            <AppRow
              mainAxisAlignment={isCurrentuser ? "end" : "start"}
              style={{ paddingLeft: theme.spacing.elementPadding, paddingRight: theme.spacing.elementPadding }}
            >
              <div onClick={handleReplyClick} style={{ cursor: "pointer", padding: "2px 4px", borderRadius: "4px" }}>
                <AppText.FootnoteSmall color={theme.primaryColor} fontWeight={600}>
                  {t(messagesCount === 1 ? "chat.reply" : "chat.replies", { count: messagesCount })}
                </AppText.FootnoteSmall>
              </div>
            </AppRow>
          </>
        )}

        {showReplyInThread && !hasReplies && onReplyInThread && (
          <>
            <SizedBox height={theme.spacing.elementPaddingSmall} />
            <AppRow
              mainAxisAlignment={isCurrentuser ? "end" : "start"}
              style={{ paddingLeft: theme.spacing.elementPadding, paddingRight: theme.spacing.elementPadding }}
            >
              <div onClick={handleReplyClick} style={{ cursor: "pointer", padding: "2px 4px", borderRadius: "4px" }}>
                <AppText.FootnoteSmall color={theme.primaryColor} fontWeight={400} style={{ opacity: 0.7 }}>
                  {t("chat.reply_in_thread")}
                </AppText.FootnoteSmall>
              </div>
            </AppRow>
          </>
        )}
      </div>
    </AppRow>
  );
};
